import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useParams } from 'react-router-dom';
import MoodleLessonPage from './MoodleLessonPage';
import { moodleFetch } from '../../../../hooks/useMoodleHooks';
import { useMoodleUi } from '../MoodleUiContext';
import { MoodleInstanceKey } from '../../../../support/slices/moodleSlice';

interface MoodleLessonProps {
  lessonId?: number;
  courseId?: string;
  instance?: MoodleInstanceKey;
}

const MoodleLesson: React.FC<MoodleLessonProps> = ({ lessonId, courseId, instance: explicitInstance }) => {
  const params = useParams<{ lessonId: string }>();
  const { config } = useMoodleUi();

  const instance = explicitInstance ?? config.instance;
  const resolvedLessonId = lessonId ?? (params.lessonId ? Number(params.lessonId) : undefined);

  const { data, isLoading, error } = useQuery({
    queryKey: ['moodle-lesson-pages', resolvedLessonId, instance],
    queryFn: async () => {
      return moodleFetch<any>(
        `moodle/lesson/${resolvedLessonId}/pages/`,
        {},
        {},
        instance
      );
    },
    ...{ enabled: !!resolvedLessonId },
  });

  const pages = useMemo(() => data?.pages ?? [], [data]);
  const warnings = data?.warnings ?? [];

  const attachments = useMemo(
    () => pages.flatMap((p: any) => (Array.isArray(p.attachments) ? p.attachments : [])),
    [pages]
  );

  const pageMap = useMemo(() => {
    const map: Record<number, any> = {};
    pages.forEach((p: any) => {
      if (p?.page?.id != null) {
        map[p.page.id] = p;
      }
    });
    return map;
  }, [pages]);

  const firstPage = useMemo(() => {
    if (!pages.length) return null;
    return pages.find((p: any) => !p.page?.prevpageid || p.page.prevpageid === 0) || pages[0];
  }, [pages]);

  const [currentPageId, setCurrentPageId] = useState<number | null>(
    firstPage?.page?.id ?? null
  );

  useEffect(() => {
    setCurrentPageId(firstPage?.page?.id ?? null);
  }, [firstPage, resolvedLessonId]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--hbr-spacing-lg)' }} aria-busy="true">
        <progress aria-label="Loading lesson pages" />
      </div>
    );
  }

  if (error) {
    return (
      <p
        role="alert"
        style={{
          color: 'red',
          fontFamily: 'var(--hbr-font-body)',
          fontSize: 'var(--hbr-font-size-md)',
          lineHeight: 'var(--hbr-font-line-height-normal)',
        }}
      >
        Error loading lesson: {String(error)}
      </p>
    );
  }

  if (!pages.length) {
    return (
      <p
        style={{
          fontFamily: 'var(--hbr-font-body)',
          fontSize: 'var(--hbr-font-size-md)',
          lineHeight: 'var(--hbr-font-line-height-normal)',
        }}
      >
        No pages in this lesson.
      </p>
    );
  }

  const renderPage = (p: any) => {
    if (p.page?.type === 'page' && Array.isArray(p.children) && p.children.length) {
      return (
        <details key={p.page.id} className="hbr-accordion">
          <summary className="hbr-accordion-summary">
            {p.page.title || p.page.name || `Page ${p.page.id}`}
          </summary>
          <div className="hbr-accordion-details">
            <MoodleLessonPage
              lessonid={resolvedLessonId!}
              page={p}
              onJump={setCurrentPageId}
              attachments={attachments}
            />
            {p.children.map((child: any) => (
              <div key={child.page?.id || child.id} className="lesson-child">
                <div className="lesson-child-title">
                  {child.page?.title || child.name || `Child ${child.id}`}
                </div>
                <MoodleLessonPage
                  lessonid={resolvedLessonId!}
                  page={child}
                  onJump={setCurrentPageId}
                  attachments={attachments}
                />
              </div>
            ))}
          </div>
        </details>
      );
    }

    return (
      <div key={p.page?.id} className="lesson-page">
        <MoodleLessonPage
          lessonid={resolvedLessonId!}
          page={p}
          onJump={setCurrentPageId}
          attachments={attachments}
        />
      </div>
    );
  };

  const currentPage = currentPageId ? pageMap[currentPageId] : firstPage;
  const prevPageId = currentPage?.page?.prevpageid || null;
  const nextPageId = currentPage?.page?.nextpageid || null;

  return (
    <div className="lesson-wrapper">
      {warnings.length > 0 && (
        <div className="warnings">
          {warnings.map((w: any, i: number) => (
            <div key={i} role="alert" className="warning">
              {w.message}
            </div>
          ))}
        </div>
      )}

      {currentPage ? renderPage(currentPage) : null}

      <div className="lesson-actions">
        <button
          type="button"
          className="nav-btn"
          onClick={() => prevPageId && setCurrentPageId(prevPageId)}
          disabled={!prevPageId || !pageMap[prevPageId]}
          aria-label="Previous page"
        >
          <span aria-hidden="true" className="nav-icon">
            ‹
          </span>
          <span className="nav-label">Previous Page</span>
        </button>

        <button
          type="button"
          className="nav-btn"
          onClick={() => nextPageId && setCurrentPageId(nextPageId)}
          disabled={!nextPageId || !pageMap[nextPageId]}
          aria-label="Next page"
        >
          <span className="nav-label">Next Page</span>
          <span aria-hidden="true" className="nav-icon">
            ›
          </span>
        </button>
      </div>

      <div className="lesson-footer">
        <span className="lesson-page-counter">
          Page {pages.findIndex((p: any) => p.page?.id === currentPage?.page?.id) + 1} of {pages.length}
        </span>
      </div>

      <style>
        {`
          .lesson-wrapper { padding: var(--hbr-spacing-sm) 0; }
          .warnings { margin-bottom: var(--hbr-spacing-md); }
          .warning {
            padding: var(--hbr-spacing-sm) var(--hbr-spacing-md);
            border-radius: 8px;
            background: #fff4e5;
            border: 1px solid #ffc107;
            color: #6a4c00;
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-sm);
            margin-bottom: var(--hbr-spacing-xs);
          }
          .lesson-page { margin-bottom: var(--hbr-spacing-md); }
          .hbr-accordion {
            margin-bottom: var(--hbr-spacing-md);
            border: 1px solid rgba(0,0,0,0.12);
            border-radius: 8px;
            background: #fff;
          }
          .hbr-accordion-summary {
            display: flex;
            align-items: center;
            list-style: none;
            cursor: pointer;
            padding: var(--hbr-spacing-sm) var(--hbr-spacing-md);
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-md);
            font-weight: var(--hbr-font-weight-semibold);
          }
          .hbr-accordion-details {
            padding: var(--hbr-spacing-sm) var(--hbr-spacing-md) var(--hbr-spacing-md);
          }
          .lesson-child {
            margin-top: var(--hbr-spacing-sm);
            margin-left: var(--hbr-spacing-md);
          }
          .lesson-child-title {
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-sm);
            font-weight: var(--hbr-font-weight-semibold);
            margin-bottom: var(--hbr-spacing-xs);
          }
          .lesson-actions {
            display: flex;
            justify-content: space-between;
            gap: var(--hbr-spacing-md);
            margin-top: var(--hbr-spacing-lg);
          }
          .nav-btn {
            display: inline-flex;
            align-items: center;
            gap: var(--hbr-spacing-xs);
            padding: 0.6rem 1rem;
            border-radius: 0.6rem;
            border: 1px solid rgba(0,0,0,0.12);
            background: #0a66ff;
            color: #fff;
            cursor: pointer;
            text-decoration: none;
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-sm);
            font-weight: var(--hbr-font-weight-semibold);
            line-height: var(--hbr-font-line-height-dense);
            transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
          }
          .nav-btn:hover:not(:disabled) {
            background: #004fe0;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .nav-btn:active:not(:disabled) {
            background: #003eb3;
          }
          .nav-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .nav-icon { font-size: var(--hbr-font-size-lg); line-height: 1; }
          .nav-label { white-space: nowrap; }
          .lesson-footer { margin-top: var(--hbr-spacing-sm); text-align: center; }
          .lesson-page-counter {
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-xs);
            color: rgba(0,0,0,0.7);
          }
        `}
      </style>
    </div>
  );
};

export default MoodleLesson;